import { Injectable } from '@angular/core';

/**
 * Central registry for scrollable containers used by features like AutoScroll.
 * Components can register their actual scroll element via the `appScrollContainer` directive.
 * This removes the need to rely on legacy element ids.
 */
@Injectable({ providedIn: 'root' })
export class ScrollContainerService {
  private stacks = new Map<string, HTMLElement[]>();
  private static readonly DEFAULT_CTX = 'default';
  private lastRegistered: HTMLElement | null = null;

  register(el: HTMLElement, context: string = ScrollContainerService.DEFAULT_CTX): void {
    const key = context || ScrollContainerService.DEFAULT_CTX;
    const arr = this.stacks.get(key) ?? [];
    arr.push(el);
    this.stacks.set(key, arr);
    this.lastRegistered = el;
  }

  unregister(el: HTMLElement, context: string = ScrollContainerService.DEFAULT_CTX): void {
    const key = context || ScrollContainerService.DEFAULT_CTX;
    const arr = this.stacks.get(key);
    if (!arr) return;
    const idx = arr.lastIndexOf(el);
    if (idx >= 0) arr.splice(idx, 1);
    if (arr.length === 0) this.stacks.delete(key);

    if (this.lastRegistered === el) {
      this.lastRegistered = this.computeLastRegistered();
    }
  }

  private computeLastRegistered(): HTMLElement | null {
    // Return the top of the newest non-empty stack if possible
    const stacks = Array.from(this.stacks.values());
    for (let i = stacks.length - 1; i >= 0; i--) {
      const s = stacks[i];
      if (s && s.length) return s[s.length - 1];
    }
    return null;
  }

  /**
   * Get the currently active container for a context (top of the stack).
   * If no context is provided, returns the last registered container, falling back
   * to the default context container if available, else the newest from any context.
   */
  getActive(context?: string): HTMLElement | null {
    if (context) {
      const arr = this.stacks.get(context);
      return (arr && arr.length) ? arr[arr.length - 1] : null;
    }
    if (this.lastRegistered) return this.lastRegistered;
    // Prefer default context, else return the last from any context
    const def = this.stacks.get(ScrollContainerService.DEFAULT_CTX);
    if (def && def.length) return def[def.length - 1];
    return this.computeLastRegistered();
  }
}
